import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import UiButton from '../ui/UiButton'

const STATUS_CLASSES = {
  active: 'text-green-600',
  pending: 'text-brand-orange',
  pending_activation: 'text-brand-orange',
  cancelled: 'text-red-600',
  finished: 'text-gray-600',
}

function SubscriptionRow({ sub, t, onCompletePayment }) {
  const status = sub.status || 'active'
  const isPendingActivation = status === 'pending_activation'
  const [completing, setCompleting] = useState(false)

  const handleCompletePayment = async () => {
    if (!onCompletePayment || completing) return
    setCompleting(true)
    try {
      const result = await onCompletePayment(sub.slug)
      if (result?.redirectUrl) {
        window.location.href = result.redirectUrl
      }
    } finally {
      setCompleting(false)
    }
  }

  return (
    <div className="border-b border-brand-blue/10 last:border-0 py-4 flex gap-4">
      <img
        src={sub.image}
        alt=""
        className="w-32 h-48 object-cover rounded-xl shrink-0"
      />
      <div className="min-w-0 flex-1">
        <h3 className="font-bold text-brand-blue text-lg">{sub.displayName}</h3>
        <p className={`font-bold uppercase text-base mt-1 ${STATUS_CLASSES[status] || 'text-brand-blue'}`}>
          {t(`library.mySubscriptions.status.${status}`, status)}
        </p>
        {isPendingActivation ? (
          <p className="text-base text-brand-orange/90 mt-1">
            {t('library.mySubscriptions.pendingActivationHint', 'Oczekuje na płatność. Dokończ ją lub anuluj, aby kupić inną subskrypcję.')}
          </p>
        ) : (
          <p className="text-base text-brand-blue/80">
            {t('library.mySubscriptions.from', { date: sub.startDate || '—' })}
          </p>
        )}
        <p className="text-base text-brand-blue/80">
          {t('library.mySubscriptions.booksCount', { count: sub.booksSentCount })}
        </p>
        {sub.favoriteItemTitle && (
          <p className="text-base text-brand-blue/80 truncate">
            {t('library.mySubscriptions.favoriteSoFar', { title: sub.favoriteItemTitle })}
          </p>
        )}
        {sub.nextDeliveryDate && (
          <p className="text-base text-brand-blue/80">
            {t('library.mySubscriptions.nextDelivery', { date: sub.nextDeliveryDate })}
          </p>
        )}
        {isPendingActivation && (
          <button
            type="button"
            onClick={handleCompletePayment}
            disabled={completing}
            className="mt-3 inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-blue rounded-full px-4 py-2 font-bold hover:bg-brand-gold/90 transition text-sm disabled:opacity-60"
          >
            {completing
              ? t('common.loading')
              : t('library.mySubscriptions.completePayment', 'Dokończ płatność')}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * "Moje subskrypcje" card for the main Moja Biblioteka dashboard.
 *
 * - 2/3 width on desktop.
 * - Scrollable list of subscriptions with poster, dates, favorite, next delivery.
 * - Gold scrollbar, no arrow buttons.
 * - "Dodaj subskrypcję" button at the bottom linking to /subscriptions/thematic.
 */
export default function MySubscriptionsCard({ subscriptions, loading, onCompletePayment }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="lg:col-span-2 bg-[var(--library-card-bg)] backdrop-blur-md rounded-[2rem] p-6 shadow-xl border border-[var(--library-border)] min-h-[400px] flex flex-col">
      <h2 className="font-climate text-2xl text-brand-blue mb-4 shrink-0">
        {t('library.mySubscriptions.title')}
      </h2>

      <div className="flex-1 overflow-y-auto max-h-[600px] gold-scrollbar pr-2 mb-4">
        {loading ? (
          <p className="text-base text-brand-blue/80">{t('common.loading')}</p>
        ) : subscriptions.length === 0 ? (
          <p className="text-base text-brand-blue/80">{t('library.mySubscriptions.empty')}</p>
        ) : (
          subscriptions.map((sub) => (
            <SubscriptionRow
              key={sub.id}
              sub={sub}
              t={t}
              onCompletePayment={onCompletePayment}
            />
          ))
        )}
      </div>

      {subscriptions.length === 0 && (
        <div className="shrink-0">
          <UiButton
            type="button"
            variant="gold"
            fullWidth
            icon={Plus}
            onClick={() => navigate('/subscription/personal')}
          >
            {t('library.mySubscriptions.addSubscription')}
          </UiButton>
        </div>
      )}
    </div>
  )
}
